// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import * as api from '../../api/client'

/**
 * D-MFR-1 — the drug grant picker.
 *
 * Extracted into one component because the grant is editable from TWO places: the
 * invitation form (new account) and the user detail panel (existing account). Two
 * copies of this logic would drift, and a drift here means one surface grants a
 * different scope than the other for the same clicks.
 *
 * The parent owns the value. This component only searches, groups and toggles.
 *
 * Grouping is by LABELER CODE — the first five digits of the NDC, FDA-assigned —
 * never by the manufacturer string. On ASP there are 163 distinct labelers but only
 * 149 distinct strings, so distinct manufacturers necessarily share a spelling; the
 * string is fine to search on and unfit to scope on.
 */
export interface DrugGrant { match_type: 'ndc' | 'labeler'; match_value: string }

interface Props {
  value: DrugGrant[]
  onChange: (next: DrugGrant[]) => void
  disabled?: boolean
}

export default function ManufacturerDrugPicker({ value, onChange, disabled }: Props) {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<api.ManufacturerDrug[]>([])
  const [loading, setLoading] = useState(true)
  const [note, setNote] = useState<string | null>(null)

  const pickedNdc = useMemo(
    () => new Set(value.filter(g => g.match_type === 'ndc').map(g => g.match_value)),
    [value])
  const pickedLabelers = useMemo(
    () => new Set(value.filter(g => g.match_type === 'labeler').map(g => g.match_value)),
    [value])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const t = setTimeout(() => {
      api.listManufacturerDrugs(search.trim() || undefined)
        .then(d => { if (!cancelled) { setResults(d.items); setNote(d.note ?? null) } })
        .catch(() => { if (!cancelled) { setResults([]); setNote('Could not load the drug list.') } })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 250)
    return () => { cancelled = true; clearTimeout(t) }
  }, [search])

  const groups = useMemo(() => {
    const m = new Map<string, { labeler: string; name: string; drugs: api.ManufacturerDrug[]; fills: number }>()
    for (const d of results) {
      const g = m.get(d.labeler) ?? { labeler: d.labeler, name: d.manufacturer || '(unnamed)', drugs: [], fills: 0 }
      g.drugs.push(d); g.fills += d.fills
      if (d.manufacturer && d.manufacturer.length > g.name.length) g.name = d.manufacturer
      m.set(d.labeler, g)
    }
    return Array.from(m.values()).sort((a, b) => b.fills - a.fills)
  }, [results])

  // Normalising here means the caller never has to: whatever was typed or clicked,
  // the stored value is hyphen-free and uppercase, matching the resolver's comparison.
  const norm = (v: string) => v.trim().replace(/-/g, '').toUpperCase()

  const setGrants = (next: DrugGrant[]) => {
    const seen = new Set<string>()
    onChange(next.filter(g => {
      const k = `${g.match_type}|${g.match_value}`
      if (seen.has(k)) return false
      seen.add(k); return true
    }))
  }

  const toggleNdc = (ndc: string) => {
    const v = norm(ndc)
    setGrants(pickedNdc.has(v)
      ? value.filter(g => !(g.match_type === 'ndc' && g.match_value === v))
      : [...value, { match_type: 'ndc', match_value: v }])
  }

  const toggleGroup = (g: { drugs: api.ManufacturerDrug[] }) => {
    const all = g.drugs.map(d => norm(d.ndc))
    const allOn = all.every(v => pickedNdc.has(v))
    setGrants(allOn
      ? value.filter(x => !(x.match_type === 'ndc' && all.includes(x.match_value)))
      : [...value, ...all.map(v => ({ match_type: 'ndc' as const, match_value: v }))])
  }

  const toggleLabeler = (labeler: string) => {
    const v = norm(labeler)
    setGrants(pickedLabelers.has(v)
      ? value.filter(g => !(g.match_type === 'labeler' && g.match_value === v))
      : [...value, { match_type: 'labeler', match_value: v }])
  }

  const drugCount = value.filter(g => g.match_type === 'ndc').length
  const mfrCount = value.filter(g => g.match_type === 'labeler').length

  return (
    <div>
      <input
        type="text" value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search NDC, drug name, brand, or manufacturer…"
        autoComplete="off" disabled={disabled}
        style={{ width: '100%', padding: '8px 11px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14 }}
      />

      {note && <div style={{ fontSize: 12, color: 'var(--color-warning, #ca8a04)', marginTop: 8 }}>{note}</div>}

      <div style={{ marginTop: 10, maxHeight: 320, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 8, background: '#fff' }}>
        {loading && groups.length === 0 ? (
          <div style={{ padding: 20, fontSize: 13, color: 'var(--color-text-mute)' }}>Loading drugs…</div>
        ) : groups.length === 0 ? (
          <div style={{ padding: 20, fontSize: 13, color: 'var(--color-text-mute)' }}>
            {search.trim() ? `No drugs match “${search.trim()}”.` : 'No dispensed drugs found for this pharmacy.'}
          </div>
        ) : groups.map(g => {
          const all = g.drugs.map(d => norm(d.ndc))
          const allOn = all.every(v => pickedNdc.has(v))
          const someOn = all.filter(v => pickedNdc.has(v)).length
          const whole = pickedLabelers.has(norm(g.labeler))
          return (
            <div key={g.labeler} style={{ borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: '#fcfcfb', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{g.name}</span>
                <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: 'var(--color-text-mute)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '1px 6px' }}>{g.labeler}</span>
                <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>
                  {someOn ? `${someOn} of ${g.drugs.length} selected` : `${g.drugs.length} drug${g.drugs.length === 1 ? '' : 's'}`}
                </span>
                <button type="button" disabled={disabled} onClick={() => toggleGroup(g)}
                  style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, cursor: disabled ? 'default' : 'pointer', border: '1px solid var(--color-primary, #0f766e)', background: '#fff', color: 'var(--color-primary, #0f766e)' }}>
                  {allOn ? 'Clear all' : `Select all ${g.drugs.length}`}
                </button>
              </div>

              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 12px 7px 26px', fontSize: 12, color: 'var(--color-text-soft, #57534e)', cursor: disabled ? 'default' : 'pointer' }}>
                <input type="checkbox" checked={whole} disabled={disabled} onChange={() => toggleLabeler(g.labeler)} style={{ marginTop: 2 }} />
                <span>Include this manufacturer&rsquo;s future drugs too &mdash; new NDCs under {g.labeler} appear automatically</span>
              </label>

              {g.drugs.map(d => (
                <label key={d.ndc} style={{
                  display: 'grid', gridTemplateColumns: '20px 1fr auto', gap: 10, alignItems: 'center',
                  padding: '7px 12px 7px 26px', borderTop: '1px solid #f5f5f4',
                  cursor: (disabled || whole) ? 'default' : 'pointer',
                  background: (whole || pickedNdc.has(norm(d.ndc))) ? '#f0fdfa' : undefined,
                  opacity: whole ? 0.6 : 1,
                }}>
                  <input type="checkbox" disabled={disabled || whole}
                    checked={whole || pickedNdc.has(norm(d.ndc))} onChange={() => toggleNdc(d.ndc)} />
                  <span style={{ fontSize: 13, minWidth: 0 }}>
                    <span style={{ fontWeight: 500 }}>{d.brand_name || d.drug_name || d.generic_name || d.ndc}</span>
                    {(d.generic_name || d.strength) && (
                      <span style={{ color: 'var(--color-text-mute)' }}> {[d.generic_name, d.strength].filter(Boolean).join(' · ')}</span>
                    )}
                    <br />
                    <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5, color: 'var(--color-text-mute)' }}>{d.ndc}</span>
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--color-text-mute)', whiteSpace: 'nowrap' }}>{d.fills} fills</span>
                </label>
              ))}
            </div>
          )
        })}
      </div>

      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>
          {mfrCount > 0 && `${mfrCount} manufacturer${mfrCount === 1 ? '' : 's'}`}
          {mfrCount > 0 && drugCount > 0 && ' + '}
          {drugCount > 0 && `${drugCount} drug${drugCount === 1 ? '' : 's'}`}
          {mfrCount === 0 && drugCount === 0 && 'Nothing selected yet'}
        </span>
        {value.length > 0 && !disabled && (
          <button type="button" onClick={() => onChange([])}
            style={{ fontSize: 12, padding: '3px 9px', borderRadius: 6, border: '1px solid var(--color-border)', background: '#fff', cursor: 'pointer' }}>
            Clear
          </button>
        )}
      </div>
    </div>
  )
}
